import { API_USER_URL } from '@/env';
import { message } from 'antd';
import { DashboardGeneralAPI, DashboardMemberCardAPI, DashboardMemberChartAPI, DashboardPackageDistributionAPI, DashboardRecentActivityAPI } from './type';

const API_USE = API_USER_URL || '';

/**
 * Fetches general dashboard overview data including total revenue, active packages, and system stats.
 * 
 * @returns {Promise<DashboardGeneralAPI>} A promise that resolves to the general dashboard data.
 * @throws {Error} Throws an error if no authentication token is found or if the API request fails.
 */
async function getDashboardGeneral(): Promise<DashboardGeneralAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/affiliate/general`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching dashboard general data');
            throw new Error('Failed to fetch dashboard general data');
        }
        const data = await response.json();
        return data as DashboardGeneralAPI;
    } catch (error) {
        message.error('Failed to fetch dashboard general data' + (error instanceof Error ? `: ${error.message}` : ''));
        throw error;
    }
}

/**
 * Fetches data for dashboard member cards identifying key member metrics.
 * 
 * @returns {Promise<DashboardMemberCardAPI>} A promise that resolves to the member card data.
 * @throws {Error} Throws an error if no authentication token is found or if the API request fails.
 */
async function getDashboardMemberCard(): Promise<DashboardMemberCardAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/affiliate/member-cards`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching dashboard member card data');
            throw new Error('Failed to fetch dashboard member card data');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        message.error('Failed to fetch dashboard member card data' + (error instanceof Error ? `: ${error.message}` : ''));
        throw error;
    }
}

/**
 * Fetches historical member growth data for chart visualization.
 * 
 * @returns {Promise<DashboardMemberChartAPI>} A promise that resolves to the member growth chart data.
 * @throws {Error} Throws an error if no authentication token is found or if the API request fails.
 */
async function getDashboardMemberChart(): Promise<DashboardMemberChartAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/affiliate/member-chart`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching dashboard member chart data');
            throw new Error('Failed to fetch dashboard member chart data');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        message.error('Failed to fetch dashboard member chart data' + (error instanceof Error ? `: ${error.message}` : ''));
        throw error;
    }
}

/**
 * Fetches data showing the distribution of various investment packages across the user base.
 * 
 * @returns {Promise<DashboardPackageDistributionAPI>} A promise that resolves to the package distribution data.
 * @throws {Error} Throws an error if no authentication token is found or if the API request fails.
 */
async function getDashboardPackageDistribution(): Promise<DashboardPackageDistributionAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/affiliate/package-dist`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching dashboard package distribution data');
            throw new Error('Failed to fetch dashboard package distribution data');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        message.error('Failed to fetch dashboard package distribution data' + (error instanceof Error ? `: ${error.message}` : ''));
        throw error;
    }
}

/**
 * Fetches a list of secondary recent activities performed in the system for admin audit.
 * 
 * @returns {Promise<DashboardRecentActivityAPI>} A promise that resolves to the recent activity list.
 * @throws {Error} Throws an error if no authentication token is found or if the API request fails.
 */
async function getDashboardRecentActivity(): Promise<DashboardRecentActivityAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/affiliate/recent`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching dashboard recent activity data');
            throw new Error('Failed to fetch dashboard recent activity data');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        message.error('Failed to fetch dashboard recent activity data' + (error instanceof Error ? `: ${error.message}` : ''));
        throw error;
    }
}

export {
    getDashboardGeneral,
    getDashboardMemberCard,
    getDashboardMemberChart,
    getDashboardPackageDistribution,
    getDashboardRecentActivity
};